import { faEllipsisVertical } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/api';
import { MessageBubble } from '@/messages/MessageBubble';
import { MessageInput } from '@/messages/MessageInput';
import { MessagesSidebarLayout } from '@/messages/MessagesSidebarLayout';
import {
  avatarFor,
  formatMessageTimestamp,
  formatThreadDate,
} from '@/messages/messagesHelpers';
import {
  ChatHeader,
  ChatHeaderSubtitle,
  ChatHeaderText,
  ChatHeaderTitle,
  EmptyState,
  MenuButton,
  MenuContainer,
  MessagesContainer,
} from '@/messages/messagesStyles';
import { ThreadDropdownMenu } from '@/messages/ThreadDropdownMenu';

export const Route = createFileRoute(
  '/_app/_users/members/$memberId/messages/view/$threadId',
)({
  component: MessagesViewRoute,
});

function MessagesViewRoute() {
  const { t } = useTranslation();
  const { memberId, threadId: threadIdParam } = Route.useParams();
  const navigate = useNavigate();
  const threadId = Number.parseInt(threadIdParam, 10);

  const [showMenu, setShowMenu] = useState(false);

  const { data: conversation } = api.getThreadMessages.useQuery(
    { path: { thread_id: threadId } },
    { enabled: Number.isFinite(threadId) },
  );

  const { mutateAsync: sendMessage } = api.sendThreadMessage.useMutation();
  const { mutateAsync: markUnread } = api.markMessageThreadUnread.useMutation();
  const { mutateAsync: hideThread } = api.hideMessageThread.useMutation();
  const { mutateAsync: deleteThread } = api.deleteMessageThread.useMutation();

  const refreshConversation = async () => {
    await Promise.all([
      api.getThreadMessages.invalidateQueries(),
      api.listMessageThreads.invalidateQueries(),
      api.getUnreadMessageCount.invalidateQueries(),
    ]);
  };

  // Opening a conversation with incoming messages marks it read.
  const { mutate: markRead } = api.markMessageThreadRead.useMutation();
  const hasIncomingMessages = !!conversation?.messages.some((m) => !m.is_you);
  useEffect(() => {
    if (Number.isFinite(threadId) && hasIncomingMessages) {
      markRead(
        { path: { thread_id: threadId } },
        {
          onSuccess: () => {
            api.listMessageThreads.invalidateQueries();
            api.getUnreadMessageCount.invalidateQueries();
          },
        },
      );
    }
  }, [threadId, hasIncomingMessages, markRead]);

  const handleSendMessage = async (message: string) => {
    if (!Number.isFinite(threadId)) return;
    await sendMessage({
      path: { thread_id: threadId },
      body: { content: message },
    });
    await refreshConversation();
  };

  return (
    <MessagesSidebarLayout
      activeThreadId={threadId}
      onThreadClick={(thread) =>
        navigate({
          to: '/members/$memberId/messages/view/$threadId/',
          params: { memberId, threadId: `${thread.id}` },
        })
      }
    >
      {conversation ? (
        <>
          <ChatHeader>
            <ChatHeaderText>
              <ChatHeaderTitle>
                {conversation.recipient.display_name}
              </ChatHeaderTitle>
              {conversation.started_at && (
                <ChatHeaderSubtitle>
                  {t('wbs.started')} {formatThreadDate(conversation.started_at)}
                </ChatHeaderSubtitle>
              )}
            </ChatHeaderText>
            {Number.isFinite(threadId) && (
              <MenuContainer>
                <MenuButton
                  // Translation Needed
                  aria-label="More options"
                  onClick={() => setShowMenu(!showMenu)}
                >
                  <FontAwesomeIcon icon={faEllipsisVertical} />
                </MenuButton>
                {showMenu && (
                  <ThreadDropdownMenu
                    onMarkUnread={async () => {
                      setShowMenu(false);
                      await markUnread({ path: { thread_id: threadId } });
                      await refreshConversation();
                    }}
                    onHideThread={async () => {
                      setShowMenu(false);
                      await hideThread({ path: { thread_id: threadId } });
                      await api.listMessageThreads.invalidateQueries();
                      navigate({
                        to: '/members/$memberId/messages/',
                        params: { memberId },
                      });
                    }}
                    onDeleteMessages={async () => {
                      setShowMenu(false);
                      await deleteThread({ path: { thread_id: threadId } });
                      await api.listMessageThreads.invalidateQueries();
                      navigate({
                        to: '/members/$memberId/messages/',
                        params: { memberId },
                      });
                    }}
                    onDeleteThread={async () => {
                      setShowMenu(false);
                      await deleteThread({ path: { thread_id: threadId } });
                      await api.listMessageThreads.invalidateQueries();
                      navigate({
                        to: '/members/$memberId/messages/',
                        params: { memberId },
                      });
                    }}
                  />
                )}
              </MenuContainer>
            )}
          </ChatHeader>

          <MessagesContainer>
            {conversation.messages.length === 0 ? (
              <EmptyState>
                {/* Translation Needed */}
                No messages yet. Say hello!
              </EmptyState>
            ) : (
              conversation.messages.map((message) => (
                <MessageBubble
                  key={message.id}
                  message={{
                    id: message.id,
                    sender: message.is_you ? t('wbs.you') : message.sender_name,
                    avatarUrl: avatarFor(
                      message.sender_name,
                      message.sender_avatar_url,
                    ),
                    content: message.content,
                    timestamp: formatMessageTimestamp(message.created_at),
                    isCurrentUser: message.is_you,
                  }}
                />
              ))
            )}
          </MessagesContainer>

          {conversation.can_send ? (
            <MessageInput onSend={handleSendMessage} />
          ) : (
            <EmptyState>
              {/* Translation Needed */}
              You can no longer send messages in this conversation.
            </EmptyState>
          )}
        </>
      ) : (
        <EmptyState>
          {/* Translation Needed */}
          Select a conversation.
        </EmptyState>
      )}
    </MessagesSidebarLayout>
  );
}
